import {
  messages,
  messagesById,
  type Response,
} from "@/utilities/message/message";
import type { Nullable } from "types/utilities";
import type { SubmissionPayload } from "@/features/betslip/services/submitBet/buildPayload";
import type { BetResponse } from "@/features/betslip/services/submitBet/types";

/**
 * How long to wait on a bet submission. Live bets are held server-side before
 * their result lands, so the first batch can take far longer than a prematch
 * submit. Both betslips submit the same message, so they share the number.
 */
export const SUBMIT_BET_TIMEOUT_MS = 60_000;

/**
 * Assert that responses from `betting-submit-slip` and its follow-ups
 * are `BetResponse`-shaped. The wire `Response.data` is `any` (see
 * `utilities/message/message.ts`) and the message bus has no per-module
 * response typing. Keeping the assertion here stops it from spreading
 * across the orchestrator.
 */
const toBetResponses = (raw: Nullable<Response[]>): BetResponse[] | null =>
  raw ? (raw as unknown as BetResponse[]) : null;

export const submitBetMessage = async (
  payload: SubmissionPayload,
  numResponses: number,
  // Fires per response as it streams in — lets the UI flip to the held
  // state before the whole batch resolves.
  onEachResponse?: (response: BetResponse) => void,
) => {
  const [raw, error, messageId] = await messages(
    "betting-submit-slip",
    payload,
    {
      numResponses,
      ignoreErrors: true,
      timeOutMs: SUBMIT_BET_TIMEOUT_MS,
      onEachResponse: onEachResponse
        ? (response: Response) =>
            onEachResponse(response as unknown as BetResponse)
        : undefined,
    },
  );
  return [toBetResponses(raw), error, messageId] as const;
};

export const fetchFollowUps = async (
  messageId: string,
  options: { numResponses: number; timeOutMs: number },
) => {
  const [raw, error] = await messagesById(messageId, {
    ...options,
    ignoreErrors: true,
  });
  return [toBetResponses(raw), error] as const;
};
